/**
 * Promo-code campaigns (#6965): the campaign shape, the parser for the raw
 * `config/PromoCampaigns` value, and the rule that turns a typed code into
 * `applied`, `unknown` or `expired`.
 *
 * Pure and dependency-free so the server (which decides) and the client (which
 * only displays what the server decided) share one definition. The parser never
 * throws and never reports: `shared/` cannot report, so it hands back what it
 * rejected and the server reports it. See design-docs/promo-campaigns.md.
 */
import { DateTime } from 'luxon';

/** The plans a campaign can cover. The values are the words used in the config. */
export enum PromoPlanName {
  Monthly = 'monthly',
  Yearly = 'yearly',
}

/** What the server answers for a code. */
export enum PromoCodeResult {
  /** The code matches a running campaign with at least one plan for this environment. */
  Applied = 'applied',
  /** No campaign has this code, or none of its plans is offered in this environment. */
  Unknown = 'unknown',
  /** The code matches a campaign whose end time has passed. */
  Expired = 'expired',
}

/** The Paddle billing environment a checkout runs in. */
export type PromoPaddleEnv = 'sandbox' | 'live';

/** A campaign's name for the page, per language. `en` is required, the others fall back to it. */
export interface PromoDisplayName {
  en: string;
  es?: string;
  nl?: string;
  fr?: string;
}

/** One plan a campaign covers. */
export interface PromoCampaignPlan {
  plan: PromoPlanName;
  /** True: the plan uses its no-trial price during the campaign (a Paddle discount cannot remove a trial). */
  dropTrial: boolean;
  /** For display only (the "40% off" badge). Paddle computes the real amount from the discount. */
  percentOff: number;
  /** The Paddle discount per environment. No id for an environment: the plan is not offered there. */
  paddleDiscountId: { sandbox?: string; live?: string };
}

/** One campaign, as stored in `config/PromoCampaigns`, after parsing. */
export interface PromoCampaign {
  /** Upper-case letters and digits, at most 32 (Paddle's own code rule). */
  code: string;
  /** ISO time. The code is refused from this moment on. */
  endsAt_utc: string;
  displayName?: PromoDisplayName;
  plans: PromoCampaignPlan[];
}

/** Something wrong in the raw config. `campaignDropped` says whether the whole entry was skipped. */
export interface PromoCampaignIssue {
  /** The entry's position in the raw list, or -1 when the value itself is not a list. */
  index: number;
  /** The entry's code, when it had a readable one. */
  code?: string;
  reason: string;
  campaignDropped: boolean;
}

export interface ParsedPromoCampaigns {
  campaigns: PromoCampaign[];
  issues: PromoCampaignIssue[];
}

/** One campaign plan as offered in a given environment: its discount id is resolved. */
export interface OfferedPromoPlan {
  plan: PromoPlanName;
  dropTrial: boolean;
  percentOff: number;
  discountId: string;
}

export type PromoCodeResolution =
  | { result: PromoCodeResult.Unknown; code: string }
  | { result: PromoCodeResult.Expired; code: string; campaign: PromoCampaign }
  | { result: PromoCodeResult.Applied; code: string; campaign: PromoCampaign; plans: OfferedPromoPlan[] };

/** Paddle's rule for a discount code, which our codes follow so one could be used there too. */
const CODE_PATTERN = /^[A-Z0-9]{1,32}$/;

/**
 * Code lengths a sale code may not have (#6984), because the Subscribe page's one
 * code field tells the kinds apart by shape before any call: 8 or 9 characters is
 * a friend's code (without or with its check character) and 16 or 17 an access
 * code, while 7 and 15 are answered on the device as "looks incomplete". A sale
 * word of one of these lengths could never reach the sale lookup.
 */
export const CODE_LENGTHS_RESERVED_FOR_OTHER_CODES: readonly number[] = [7, 8, 9, 15, 16, 17];

const DISPLAY_LANGUAGES = ['en', 'es', 'nl', 'fr'] as const;

/**
 * The code as the server compares it: trimmed and upper-case. Returns undefined
 * for a missing or blank input, which means "no code was given" rather than
 * "an unknown code".
 */
export function normalisePromoCode(input: string | undefined | null): string | undefined {
  if (typeof input !== 'string') return undefined;
  const trimmed = input.trim().toUpperCase();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The code in its normalised form when it could be a campaign code at all (1 to 32
 * letters or digits), else undefined. For values from outside, such as a code the
 * browser stamped into a Paddle checkout's custom data.
 */
export function wellFormedPromoCode(input: unknown): string | undefined {
  const code = normalisePromoCode(typeof input === 'string' ? input : undefined);
  return code && CODE_PATTERN.test(code) ? code : undefined;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Read one plan entry, or return the reason it cannot be used. */
function parsePlan(raw: unknown): PromoCampaignPlan | string {
  if (!raw || typeof raw !== 'object') return 'a plan entry is not an object';
  const entry = raw as Record<string, unknown>;
  if (entry.plan !== PromoPlanName.Monthly && entry.plan !== PromoPlanName.Yearly) {
    return `plan "${String(entry.plan)}" is not monthly or yearly`;
  }
  if (typeof entry.dropTrial !== 'boolean') return `plan ${entry.plan}: dropTrial is not true or false`;
  const percentOff = entry.percentOff;
  if (typeof percentOff !== 'number' || !Number.isFinite(percentOff) || percentOff <= 0 || percentOff >= 100) {
    return `plan ${entry.plan}: percentOff is not a number between 0 and 100`;
  }
  const ids = entry.paddleDiscountId;
  if (!ids || typeof ids !== 'object') return `plan ${entry.plan}: paddleDiscountId is not an object`;
  const { sandbox, live } = ids as Record<string, unknown>;
  if (sandbox !== undefined && typeof sandbox !== 'string') return `plan ${entry.plan}: paddleDiscountId.sandbox is not text`;
  if (live !== undefined && typeof live !== 'string') return `plan ${entry.plan}: paddleDiscountId.live is not text`;
  return {
    plan: entry.plan,
    dropTrial: entry.dropTrial,
    percentOff,
    // an empty id means "not offered in that environment", the same as a missing one
    paddleDiscountId: {
      sandbox: isNonEmptyString(sandbox) ? sandbox.trim() : undefined,
      live: isNonEmptyString(live) ? live.trim() : undefined,
    },
  };
}

/**
 * Read the display name. A bad name never drops the campaign: the page falls back
 * to generic wording, and the problem is reported. Languages that are not text are
 * left out; a name without `en` is left out entirely.
 */
function parseDisplayName(raw: unknown, problems: string[]): PromoDisplayName | undefined {
  if (raw === undefined) return undefined;
  if (!raw || typeof raw !== 'object') {
    problems.push('displayName is not an object; generic wording is used');
    return undefined;
  }
  const entry = raw as Record<string, unknown>;
  const name: Partial<PromoDisplayName> = {};
  for (const language of DISPLAY_LANGUAGES) {
    const value = entry[language];
    if (value === undefined) continue;
    if (isNonEmptyString(value)) {
      name[language] = value.trim();
    } else {
      problems.push(`displayName.${language} is not text; it falls back to English`);
    }
  }
  if (!name.en) {
    problems.push('displayName has no English name; generic wording is used');
    return undefined;
  }
  return name as PromoDisplayName;
}

/** Read one campaign entry. Returns the campaign, or the reason the whole entry is skipped. */
function parseCampaign(raw: unknown, problems: string[]): PromoCampaign | string {
  if (!raw || typeof raw !== 'object') return 'the entry is not an object';
  const entry = raw as Record<string, unknown>;
  const code = wellFormedPromoCode(entry.code);
  if (!code) return `code "${String(entry.code)}" is not 1 to 32 letters or digits`;
  if (CODE_LENGTHS_RESERVED_FOR_OTHER_CODES.includes(code.length)) {
    return `code ${code} is ${code.length} characters long, so it could be taken for a friend's or access code`;
  }
  if (typeof entry.endsAt_utc !== 'string' || !DateTime.fromISO(entry.endsAt_utc, { zone: 'utc' }).isValid) {
    return 'endsAt_utc is not a valid ISO time';
  }
  if (!Array.isArray(entry.plans) || entry.plans.length === 0) return 'plans is not a list with at least one plan';
  const plans: PromoCampaignPlan[] = [];
  for (const rawPlan of entry.plans) {
    const plan = parsePlan(rawPlan);
    // one damaged plan drops the campaign: offering only part of a sale would
    // silently change what marketing announced
    if (typeof plan === 'string') return plan;
    if (plans.some(p => p.plan === plan.plan)) return `plan ${plan.plan} is listed twice`;
    plans.push(plan);
  }
  return {
    code,
    endsAt_utc: entry.endsAt_utc,
    displayName: parseDisplayName(entry.displayName, problems),
    plans,
  };
}

/**
 * Parse the raw global value of `config/PromoCampaigns`. A damaged entry is
 * skipped and listed in `issues`, never thrown, so one typo cannot stop every
 * checkout. A missing value (no document) means no campaigns and no issues.
 */
export function parsePromoCampaigns(raw: unknown): ParsedPromoCampaigns {
  const campaigns: PromoCampaign[] = [];
  const issues: PromoCampaignIssue[] = [];
  if (raw === undefined || raw === null) return { campaigns, issues };
  if (!Array.isArray(raw)) {
    issues.push({ index: -1, reason: 'the value is not a list of campaigns', campaignDropped: true });
    return { campaigns, issues };
  }
  for (const [index, rawEntry] of raw.entries()) {
    const rawCode = rawEntry && typeof rawEntry === 'object' ? (rawEntry as Record<string, unknown>).code : undefined;
    const code = typeof rawCode === 'string' ? rawCode : undefined;
    const problems: string[] = [];
    const campaign = parseCampaign(rawEntry, problems);
    if (typeof campaign === 'string') {
      issues.push({ index, code, reason: campaign, campaignDropped: true });
      continue;
    }
    if (campaigns.some(c => c.code === campaign.code)) {
      // the first entry wins, so adding a second one by mistake cannot change a running sale
      issues.push({ index, code, reason: `code ${campaign.code} is already used by an earlier entry`, campaignDropped: true });
      continue;
    }
    for (const reason of problems) issues.push({ index, code, reason, campaignDropped: false });
    campaigns.push(campaign);
  }
  return { campaigns, issues };
}

/**
 * Turn a code into the server's answer, in this order: match the code (ignoring
 * case); keep only the plans with a discount id for this Paddle environment (none
 * left: unknown); then compare the end time with now (past it: expired). So a live
 * request with a sandbox-only test code is always unknown, expired or not.
 *
 * Returns undefined when no code was given at all.
 */
export function resolvePromoCode(
  campaigns: PromoCampaign[],
  input: string | undefined | null,
  env: PromoPaddleEnv,
  nowMillis: number,
): PromoCodeResolution | undefined {
  const code = normalisePromoCode(input);
  if (!code) return undefined;
  const campaign = campaigns.find(c => c.code === code);
  if (!campaign) return { result: PromoCodeResult.Unknown, code };
  const plans: OfferedPromoPlan[] = [];
  for (const plan of campaign.plans) {
    const discountId = plan.paddleDiscountId[env];
    if (discountId) plans.push({ plan: plan.plan, dropTrial: plan.dropTrial, percentOff: plan.percentOff, discountId });
  }
  if (plans.length === 0) return { result: PromoCodeResult.Unknown, code };
  const endsAtMillis = DateTime.fromISO(campaign.endsAt_utc, { zone: 'utc' }).toMillis();
  if (nowMillis >= endsAtMillis) return { result: PromoCodeResult.Expired, code, campaign };
  return { result: PromoCodeResult.Applied, code, campaign, plans };
}

/**
 * Keep only the campaign plans that can really be sold with their discount here.
 * A plan that drops its trial needs its no-trial price in this environment, because
 * its discount is restricted to that price and Paddle refuses it on any other. A
 * plan that keeps its trial can always be sold.
 *
 * When none is left the answer becomes `unknown`: a code that cannot discount
 * anything must not move the buyer to Paddle or tell them it was applied. The
 * removed plans are returned for the caller to report, since it is a config error.
 */
export function limitToSellablePlans(
  resolution: PromoCodeResolution | undefined,
  hasNoTrialPrice: (plan: PromoPlanName) => boolean,
): { resolution: PromoCodeResolution | undefined; unsellable: PromoPlanName[] } {
  if (resolution?.result !== PromoCodeResult.Applied) return { resolution, unsellable: [] };
  const sellable = resolution.plans.filter(p => !p.dropTrial || hasNoTrialPrice(p.plan));
  const unsellable = resolution.plans.filter(p => !sellable.includes(p)).map(p => p.plan);
  if (sellable.length === 0) return { resolution: { result: PromoCodeResult.Unknown, code: resolution.code }, unsellable };
  return { resolution: unsellable.length > 0 ? { ...resolution, plans: sellable } : resolution, unsellable };
}

/**
 * The campaign's name in the given language (`es`, `es-ES`, …), falling back to
 * English. Undefined when the campaign has no name, so the page uses generic wording.
 */
export function promoDisplayName(campaign: PromoCampaign, language: string | undefined): string | undefined {
  const name = campaign.displayName;
  if (!name) return undefined;
  const short = (language ?? '').slice(0, 2).toLowerCase();
  const match = DISPLAY_LANGUAGES.find(l => l === short);
  return (match && name[match]) || name.en;
}
